import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import {
  TimeSlots,
  TimeSlotsApi,
  NewTimeSlots,
  TimeSlotsFilter,
} from '../models';
import { mapTimeSlotsApiToModel } from '../adapters';
import { API_BUSINESS_URL } from '@/utils';

const timeSlotsService = createApi({
  reducerPath: 'timeSlotsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/time-slots`),
  tagTypes: ['TimeSlots'],
  endpoints: (builder) => ({
    getTimeSlots: builder.query<
      PaginatedResponse<TimeSlots>,
      {
        paginated: PaginatedRequest;
        filters?: TimeSlotsFilter;
      }
    >({
      query: ({ paginated, filters }) => ({
        url: '',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters?.code &&
            filters.code.trim() !== '' && { code: filters.code.trim() }),
          ...(filters?.dateTimeFrom &&
            filters.dateTimeFrom.trim() !== '' && {
              dateTimeFrom: filters.dateTimeFrom.trim(),
            }),
          ...(filters?.dateTimeTo &&
            filters.dateTimeTo.trim() !== '' && {
              dateTimeTo: filters.dateTimeTo.trim(),
            }),
          ...(filters?.logisticOperatorId && {
            logisticOperatorId: filters.logisticOperatorId,
          }),
          ...(filters?.includeAncestors && { includeAncestors: true }),
          ...(filters?.expiration && { expiration: filters.expiration }),
          ...(filters?.active !== undefined && { active: filters.active }),
          ...(filters?.mo !== undefined && { mo: filters.mo }),
          ...(filters?.tu !== undefined && { tu: filters.tu }),
          ...(filters?.we !== undefined && { we: filters.we }),
          ...(filters?.th !== undefined && { th: filters.th }),
          ...(filters?.fr !== undefined && { fr: filters.fr }),
          ...(filters?.sa !== undefined && { sa: filters.sa }),
          ...(filters?.su !== undefined && { su: filters.su }),
        },
      }),
      transformResponse: (response: PaginatedResponse<TimeSlotsApi>) => {
        if (!response) {
          return { count: 0, items: [] };
        }

        const items = Array.isArray(response.items) ? response.items : [];

        return {
          ...response,
          items: items.map(mapTimeSlotsApiToModel),
        };
      },
      providesTags: ['TimeSlots'],
    }),
    getTimeSlotById: builder.query<TimeSlots, { timeSlotId: string }>({
      query: ({ timeSlotId }) => ({
        url: `/${timeSlotId}`,
      }),
      transformResponse: (response: TimeSlotsApi) =>
        mapTimeSlotsApiToModel(response),
      providesTags: ['TimeSlots'],
    }),
    createTimeSlot: builder.mutation<TimeSlots, NewTimeSlots>({
      query: (body) => ({
        url: '',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TimeSlots'],
    }),
    updateTimeSlot: builder.mutation<
      TimeSlots,
      { timeSlotId: string; body: Partial<TimeSlots> }
    >({
      query: ({ timeSlotId, body }) => ({
        url: `/${timeSlotId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['TimeSlots'],
    }),
    deleteTimeSlot: builder.mutation<void, { timeSlotId: string }>({
      query: ({ timeSlotId }) => ({
        url: `/${timeSlotId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TimeSlots'],
    }),
  }),
});

export default timeSlotsService;
export const {
  useGetTimeSlotsQuery,
  useGetTimeSlotByIdQuery,
  useCreateTimeSlotMutation,
  useUpdateTimeSlotMutation,
  useDeleteTimeSlotMutation,
} = timeSlotsService;
